import { ReactElement, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { AppShell, ActionIcon, Box } from '@mantine/core';
import { IconLayoutSidebarLeftCollapse, IconLayoutSidebarLeftExpand } from '@tabler/icons-react';

import { useFeatureFlag } from '@/store/slices/api/featureFlagsApiSlice';
import SupportWidget from '@/components/SupportWidget';
import { SideNav } from '@/components/SideNav';
import { TopNav } from '@/components/TopNav';
import { useNavigationV2FeatureFlag } from '@/hooks/useNavigationV2FeatureFlag';
import { TOP_NAV_HEIGHT } from '@/utils/constants';
import { routes } from '@/routes';

const SIDEBAR_OPEN_WIDTH = 220;
const SIDEBAR_COLLAPSED_WIDTH = 60;
const ICON_STROKE = 1.5;

export function MainAppShell({ children }: Readonly<{ children: ReactElement }>) {
  const [collapsed, setCollapsed] = useState(false);
  const location = useLocation();
  const isSupportWidgetEnabled = useFeatureFlag('supportWidget');
  const { isNavigationV2Enabled: isSideNavEnabled } = useNavigationV2FeatureFlag();

  const navbarWidth = collapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_OPEN_WIDTH;

  const isTransmissionPage = location.pathname.includes(routes.physician.defaultTransmissionReport);

  // Auto-collapse when first entering the Transmission page; excluded `collapsed` intentionally
  // so a user manually re-expanding the nav isn't immediately collapsed again on re-render.
  useEffect(() => {
    if (isSideNavEnabled && isTransmissionPage && !collapsed) {
      setCollapsed(true);
    }
  }, [isTransmissionPage, isSideNavEnabled]);

  // Expose the live navbar width so viewport-fixed overlays can sit just outside the nav.
  useEffect(() => {
    if (isSideNavEnabled) {
      document.documentElement.style.setProperty('--app-navbar-width', `${navbarWidth}px`);
    }
  }, [navbarWidth, isSideNavEnabled]);

  // ── TopNav layout (flag off) ──────────────────────────────────────────────
  if (!isSideNavEnabled) {
    const isTransmissionReportPage =
      location.pathname.includes(routes.physician.reviewTransmissions) ||
      location.pathname.includes(routes.physician.editReport) ||
      /^\/physician\/transmissions\/\d+\/edit$/.test(location.pathname) ||
      location.pathname.includes(routes.physician.loadReport) ||
      /^\/physician\/transmissions\/\d+\/review$/.test(location.pathname);

    return (
      <AppShell
        transitionDuration={500}
        transitionTimingFunction="ease"
        header={{ height: TOP_NAV_HEIGHT, collapsed: isTransmissionReportPage }}
        padding="md">
        <AppShell.Header bg="plum.9" style={{ transition: 'height 0.3s ease-in-out' }}>
          <TopNav />
        </AppShell.Header>

        <AppShell.Main
          bg="plum.0"
          px={0}
          pt={54}
          pb={0}
          style={{ transition: 'padding-top 0.3s ease-in-out' }}>
          {children}
        </AppShell.Main>

        {isSupportWidgetEnabled && <SupportWidget />}
      </AppShell>
    );
  }

  // ── SideNav layout (flag on) ──────────────────────────────────────────────
  return (
    <AppShell
      navbar={{ width: navbarWidth, breakpoint: 'sm' }}
      padding={0}
      transitionDuration={200}
      transitionTimingFunction="ease">

      <AppShell.Navbar
        bg="plum.8"
        style={{
          borderRight: 'none',
          transition: 'width 0.2s ease',
          overflow: 'visible',
          width: navbarWidth,
        }}>
        <Box style={{ height: '100%', minHeight: 0, overflowY: 'auto' }}>
          <SideNav collapsed={collapsed} />
        </Box>

        <Box
          style={{
            position: 'absolute',
            bottom: 88,
            right: -14,
            zIndex: 10,
          }}>
          <ActionIcon
            size={28}
            radius="xl"
            variant="filled"
            onClick={() => setCollapsed((c) => !c)}
            style={{
              background: '#3B1A2C',
              border: '1px solid rgba(255,255,255,0.15)',
            }}>
            {collapsed ? (
              <IconLayoutSidebarLeftExpand size={14} stroke={ICON_STROKE} color="rgba(255,255,255,0.7)" />
            ) : (
              <IconLayoutSidebarLeftCollapse size={14} stroke={ICON_STROKE} color="rgba(255,255,255,0.7)" />
            )}
          </ActionIcon>
        </Box>
      </AppShell.Navbar>

      <AppShell.Main
        bg="plum.0"
        style={{ minHeight: '100dvh', minWidth: 0, display: 'flex', flexDirection: 'column', overflowX: 'auto' }}>
        {children}
      </AppShell.Main>

      {isSupportWidgetEnabled && <SupportWidget />}
    </AppShell>
  );
}
